import type { JSX } from 'react';
import { usePref } from '../lib/prefs.js';
import { SettingsRow } from '../components/Settings.js';
import { SettingsMenu } from '../components/SettingsControls.js';
import { unmetRequirement } from './assertBinding.js';
import type { ViewBinding, ViewType } from './contract.js';
import { forgetScreenSession, pinnedViewPrefKey } from './useScreen.js';

/**
 * Ligne de reglage "vue par defaut" d'un ecran, fournie par le SOCLE.
 *
 * Elle vit dans le module `settings.tsx` de l'app, monte par le hub Parametres
 * du shell. Le but est qu'aucune app ne recode ce reglage : les 40 apps a venir
 * posent la meme ligne, avec les memes mots et le meme comportement.
 *
 * Deux regimes, et c'est tout le sujet :
 * - vide (le defaut) : "Derniere vue utilisee", la memoire automatique du core.
 *   L'ecran rouvre sur ce que l'utilisateur regardait la derniere fois ;
 * - une vue choisie : elle est EPINGLEE. L'ecran rouvre toujours dessus, meme
 *   si l'utilisateur en a regarde une autre entre-temps.
 *
 * Seules les vues de COLLECTION sont proposees : la fiche porte un element
 * precis, elle ne peut pas etre une vue d'arrivee.
 *
 * ET SEULES LES VUES REELLEMENT NOURRIES SONT CHOISISSABLES : quand l'ecran
 * fournit ses `bindings`, une vue au role manquant (celle que le selecteur de
 * la zone 3 grisera) reste LISTEE mais grisee ici aussi, avec sa raison dans
 * le menu. Jamais absente : "pourquoi mon kanban n'est pas propose" ne doit
 * pas devenir un bug silencieux ; jamais epinglable : epingler une vue que
 * l'ecran grise ouvrirait l'app sur une impasse.
 *
 * Une ligne par porte quand `doorId` est fourni, sinon par ecran.
 * Chaque cible utilise son propre catalogue de vues.
 *
 * @example
 * <SettingsSection title="Mon affichage">
 *   <ViewDefaultSetting
 *     appId="contact"
 *     screenId="contacts"
 *     types={CONTACT_VIEW_TYPES}
 *     bindings={CONTACT_BINDINGS}
 *   />
 * </SettingsSection>
 */
export interface ViewDefaultSettingProps<T> {
  /** Id de l'app, identique a celui passe a `useScreen`. */
  appId: string;
  /** Id de l'ecran, identique a celui passe a `useScreen`. */
  screenId: string;
  /** Porte visee. Absent : preference historique de l’ecran sans portes. */
  doorId?: string;
  /** Le catalogue de vues de CET ecran, le meme tableau que `useScreen`. */
  types: ViewType[];
  /**
   * Les bindings de CET ecran, les memes que ceux passes a `useScreen`.
   * Avec eux, une vue non nourrie (role manquant) est grisee avec sa raison,
   * au lieu d'etre epinglable puis grisee par l'ecran. Sans eux, toutes les
   * vues collection restent proposees (comportement historique).
   */
  bindings?: ReadonlyArray<ViewBinding<T>>;
  /** Libelle de la ligne. Defaut : "Vue par defaut". */
  label?: string;
  /** Precision sous le libelle. Un defaut parlant est fourni. */
  hint?: string;
}

const LAST_USED = '';

/**
 * La raison d'un grisage, reduite a son CONSTAT : `unmetRequirement` parle au
 * developpeur en deux temps (le constat, puis la consigne "ajoute..."), une
 * option de liste n'a la place que du premier. On coupe donc a la premiere
 * phrase, puis avant la consigne quand elle est accolee par " : ajoute".
 * Si la formulation des messages change, la coupe degrade sans casser : le
 * texte complet de la raison reste affiche.
 */
function shortReason(reason: string): string {
  const sentence = reason.split('. ')[0] ?? reason;
  const constat = (sentence.split(' : ajoute')[0] ?? sentence).replace(/\.$/, '');
  return constat.charAt(0).toLowerCase() + constat.slice(1);
}

export function ViewDefaultSetting<T = never>({
  appId,
  screenId,
  doorId,
  types,
  bindings,
  label = 'Vue par défaut',
  hint,
}: ViewDefaultSettingProps<T>): JSX.Element {
  const [pinned, setPinned] = usePref<string | null>(pinnedViewPrefKey(appId, screenId, doorId), null);

  const collections = types.filter((type) => type.level === 'collection');

  /** Pourquoi cette vue serait grisee par l'ecran, ou null si elle est nourrie. */
  function reasonOf(type: ViewType): string | null {
    if (bindings == null) return null;
    return unmetRequirement(
      type,
      bindings.find((candidate) => candidate.type === type.id),
    );
  }

  // Une pref qui designe une vue disparue du catalogue est ignoree, jamais
  // affichee : l'app a pu retirer une vue depuis que l'utilisateur a choisi.
  // Une pref qui designe une vue devenue non nourrie reste affichee (l'option
  // grisee peut etre l'option courante) : la verite d'abord, et en changer
  // reste possible.
  const value = collections.some((type) => type.id === pinned) ? String(pinned) : LAST_USED;

  function choose(next: string): void {
    setPinned(next === LAST_USED ? null : next);
    // Oublie la reprise de cette cible, notamment la fiche ouverte :
    // le prochain montage applique le choix de vue tout juste pose.
    forgetScreenSession(appId, screenId, doorId);
  }

  if (collections.length <= 1) {
    return (
      <SettingsRow label={label} hint={hint}>
        <span className="text-[0.8125rem] text-ink-400">
          {collections[0]?.label ?? 'Aucune vue'}
        </span>
      </SettingsRow>
    );
  }

  return (
    <SettingsRow label={label} hint={hint}>
      <SettingsMenu
        value={value}
        onChange={choose}
        label={`Vue par défaut de ${doorId ?? screenId}`}
        options={[
          { value: LAST_USED, label: 'Dernière vue utilisée' },
          ...collections.map((type) => {
            const reason = reasonOf(type);
            return reason != null
              ? { value: type.id, label: type.label, detail: shortReason(reason), inactif: reason }
              : { value: type.id, label: type.label };
          }),
        ]}
      />
    </SettingsRow>
  );
}
